// modules
import React, { useLayoutEffect, useRef } from 'react';
import { RouteComponentProps } from 'react-router-dom';

// components
import { AppAccessDenied } from './AppAccessDenied';
import { AppPermissionsProvider } from '../../~reusables/contexts/AppPermissionsContext';

// logic
import { useNavigation } from '../../~reusables/contexts/NavigationContext';
import { useStore, setGlobalLoading } from '../../store';

interface IProtectedApp extends RouteComponentProps {
  component: React.FC<RouteComponentProps>;
}

export const ProtectedApp: React.FC<IProtectedApp> = ({
  component: Component,
  ...routeProps
}) => {
  const {
    navState: { appLevelNav },
  } = useNavigation();
  const {
    appsLoaded,
    hasBuildsDataLoaded,
    selectedAppId,
    applicationLoadFailed,
  } = useStore((state) => ({
    appsLoaded: state.appsLoaded,
    hasBuildsDataLoaded: state.buildState.hasBuildsDataLoaded,
    selectedAppId: state.selectedApp,
    applicationLoadFailed: state.applicationLoadFailed,
  }));

  // Extract app ID from URL to detect when we're navigating to a different app
  const urlAppId = routeProps.match?.params
    ? (routeProps.match.params as { id?: string }).id
    : null;

  // The page only needs its own app, and the app it's a child of (which bills
  // for it), not every team's apps
  const { hasApp, hasParentApp } = useStore((state) => {
    const app = state.apps.find(({ id }) => id === urlAppId);
    const parentId = app?.parent?.id;
    return {
      hasApp: Boolean(app),
      hasParentApp:
        !parentId ||
        state.appsLoaded ||
        state.apps.some(({ id }) => id === parentId),
    };
  });

  // If URL app ID doesn't match selected app, we're switching apps and should show loading
  const isAppSwitching = Boolean(urlAppId && urlAppId !== selectedAppId);

  // A URL that is absent after every account's apps loaded is not an app
  // switch in progress. Do not wait for builds or retain cached app content
  // once access is gone.
  const isUnavailable = appsLoaded && !hasApp;
  const hasDataLoaded = hasApp && hasParentApp && hasBuildsDataLoaded;
  const isLoading =
    !isUnavailable &&
    (appLevelNav.showLoader || !hasDataLoaded || isAppSwitching);
  const lastContentRef = useRef<React.ReactNode>(null);
  const lastAppIdRef = useRef<string | null>(null);

  // Update global loading state synchronously before paint
  useLayoutEffect(() => {
    setGlobalLoading(isLoading);
  }, [isLoading]);

  if (isUnavailable) {
    lastContentRef.current = null;
    lastAppIdRef.current = null;
    return <AppAccessDenied loadFailed={applicationLoadFailed} />;
  }

  // If loading, show the last content only if it's for the same app
  // When switching apps, show nothing (just the loading bar)
  if (isLoading) {
    const isSameApp = lastAppIdRef.current === urlAppId;
    if (isSameApp && lastContentRef.current) {
      return <>{lastContentRef.current}</>;
    }
    return null;
  }

  const content = (
    <AppPermissionsProvider>
      <Component {...routeProps} />
    </AppPermissionsProvider>
  );

  // Store current content and app ID for next loading state
  lastContentRef.current = content;
  lastAppIdRef.current = urlAppId ?? null;

  return <>{content}</>;
};
